import type { Header } from '@tanstack/react-table';
import TableTooltip from '@/components/Table/components/TableTooltip';
import { DeltaUnit } from '@/components/Table/utils/DeltaUnit';
import { ValueWithUnit } from '@/components/Table/utils/ValueWithUnit';
import { Skeleton } from '@/components/ui/skeleton';
import type { SeriesDefinition } from '@/types/statisticsSeriesDefs';
import { cn } from '@/utils/ui-utils';
import SortButton from '../../components/SortButton';
import type { CellValue, DynamicTable, DynamicTableRow, NumericCellValue, Period } from '.';

const getPeriodLabel = (period: Period | undefined, isDelta: boolean): string => {
  if (!period) return '';
  if (isDelta && period.delta) return `${period.delta.start} – ${period.delta.end}`;
  return `${period.start} – ${period.end}`;
};

const TooltipContent = ({
  series,
  isDelta,
  periodLabel,
}: {
  series: SeriesDefinition;
  isDelta: boolean;
  periodLabel: string;
}) => {
  if (!series.title && !series.description && !periodLabel) return null;
  return (
    <div className='flex max-w-56 flex-col gap-2'>
      {series.title && (
        <div className='flex gap-2'>
          <p className='text-sm leading-none font-semibold'>{series.title}</p>
          {isDelta && <p className='text-xs opacity-60'>(Jämförelsevärde)</p>}
        </div>
      )}
      {series.description && <p className='text-xs leading-relaxed opacity-90'>{series.description}</p>}
      {periodLabel && (
        <>
          <hr className='border-white/20' />
          <p className='text-xs opacity-60'>{periodLabel}</p>
        </>
      )}
    </div>
  );
};

export const ColumnHeader = <TData, TValue>({
  series,
  data,
  periods,
  header,
}: {
  series: SeriesDefinition;
  data?: DynamicTableRow[];
  periods?: DynamicTable['periods'];
  header: Header<TData, TValue>;
}) => {
  const isDelta = series.slug.includes(':delta');
  const value = data?.length ? (data[0][series.slug] as CellValue) : undefined;
  const periodLabel = getPeriodLabel(periods?.at(0), isDelta);

  return (
    <div className={cn('flex h-10 w-full flex-col gap-0.5 text-left', isDelta && 'items-end justify-center')}>
      {!isDelta && (
        <div>
          <TableTooltip
            description={
              <TooltipContent
                series={series}
                isDelta={isDelta}
                periodLabel={periodLabel}
              />
            }
            align={'center'}
            className='px-4 py-3'
          >
            <div className='hover:text-muted-foreground pointer-events-auto inline-flex items-center gap-1'>
              <span>{series.shortname}</span>
              {header.column.getCanSort() && (
                <SortButton
                  column={header.column}
                  isSorted={header.column.getIsSorted()}
                  className='text-xs'
                />
              )}
            </div>
          </TableTooltip>
          {data && (
            <ValueWithUnit
              value={value}
              unit={series.unit}
              className='w-full truncate text-xs font-normal'
            />
          )}
        </div>
      )}

      {!data?.length ? (
        <Skeleton className='mt-1 h-2 w-8' />
      ) : (
        isDelta && (
          <TableTooltip
            description={
              <TooltipContent
                series={series}
                isDelta={isDelta}
                periodLabel={periodLabel}
              />
            }
            align={'center'}
            className='px-4 py-3'
          >
            <div className='pointer-events-auto'>
              <DeltaUnit
                value={value as NumericCellValue}
                unit={series.unit}
                className='text-xs font-normal'
                sort={
                  <SortButton
                    column={header.column}
                    isSorted={header.column.getIsSorted()}
                    className='text-primary text-xs'
                  />
                }
              />
            </div>
          </TableTooltip>
        )
      )}
    </div>
  );
};
